import TagManager from 'react-gtm-module';
import qs from 'qs';
import uniqBy from 'lodash/uniqBy';
import clientConfig from '../../clientConfig';
import { isEnv, isNode, siteTypesEnum } from '../../helpers';
import parsePurchasedProductsForGTM from '../parsePurchasedProductsForGTM';
import { UniqueIdentifier } from '../../types';
import { AudienceHubOptions } from '../../components/hub/audienceHub/store/features/options';
import { BrandHubOptions } from '../../components/hub/brandHub/store/features/options';
import {
  PricingProductsMeta,
  PricingProductType,
} from '../../components/paymentJourneyRedesign/store/features/products/types';
import {
  PricingOrderReceipt,
  PricingOrderReceiptPackageSelection,
  PricingOrderReceiptTicketPackages,
} from '../../components/paymentJourneyRedesign/store/features/orderReceipt/types';
import {
  EventCategoryGA,
  GA4EventCategories,
  GA4EventNames,
  LegacyEventKeys,
  PushValue,
  ReduxStore,
  VariablesEventKeys,
  GenerateLeadType,
  CTAClickType,
  GA4EventLabels,
} from './types';
import {
  pushLegacyUAEventToDataLayer,
  getCookieValue,
  pushGA4Event,
  formatEventLabel,
  initGA4Context,
  compact,
} from './helpers';

const { EVENT, COURSE, BRAND_HUB, AUDIENCE_HUB, ARTICLE, L1_TOPIC_HUB } =
  siteTypesEnum();

declare global {
  interface Window {
    __REDUX_STORE__?: ReduxStore;
    dataLayer?: Array<unknown>;
    google_tag_manager?: UniqueIdentifier[];
    googletag: {
      pubads?: () => {
        setPrivacySettings: (settings: {
          childDirectedTreatment: boolean;
          underAgeOfConsent: boolean;
          nonPersonalizedAds: boolean;
        }) => void;
      };
      cmd?: {
        push: (func: () => void) => void;
      };
    };
  }
}

class GTMService {
  public gtm = TagManager;
  private counter?: number;

  private googleTagManagerConfig?: typeof clientConfig.googleTagManagerConfig;
  private googleTagManagerDataLayerConfig?: typeof clientConfig.googleTagManagerConfig.dataLayer;
  public googleTagManagerContainers: TagManager.TagManagerArgs[] = [];

  constructor() {
    this.counter = 0;
  }

  public getAppState() {
    return window.__REDUX_STORE__ as ReduxStore;
  }

  pushSegment(name: Array<string>) {
    // UA: Convert segments array to a string for backwards compatibility with Universal Analytics (UA) / GTM
    const segmentsArray = name.map((segment) => decodeURIComponent(segment));
    const segmentsForUA = `[${segmentsArray.join(', ')}]`;

    // Push legacy UA event - UA expects a plain string variable, not an array
    pushLegacyUAEventToDataLayer(VariablesEventKeys.SEGMENTS, segmentsForUA);

    // GA4: Push a structured event with a proper array for user segments
    // GA4 supports arrays and expects all parameters to be part of an event
    pushGA4Event({
      event: GA4EventNames.USER_SEGMENT_SET,
      value: segmentsArray,
      event_category: GA4EventCategories.USER,
      event_label: GA4EventLabels.USER_SEGMENTS_FROM_MA_CONFIG,
    });
  }

  updateSegmentsLayer(segmentName: string) {
    const gtmContainerId = this.googleTagManagerContainers?.[0]?.gtmId;

    if (gtmContainerId && segmentName) {
      const gtmContainer = (window.google_tag_manager as Record<string, any>)?.[
        gtmContainerId
      ];
      const segmentsDataLayer = gtmContainer?.dataLayer?.get('segments');

      let segmentsArray = segmentsDataLayer.replace(/[[\]\s]/g, '') ?? [];

      if (segmentsArray.length) {
        segmentsArray = segmentsArray.split(',');
      }

      segmentsArray.push(segmentName);

      const segmentsListString = `[${segmentsArray}]`;

      (window.google_tag_manager as Record<string, any>)?.[
        gtmContainerId
      ]?.dataLayer?.set('segments', segmentsListString);

      const dataLayerItem = window.dataLayer?.find(
        (layer: any) => layer?.segments !== undefined,
      );
      if (dataLayerItem) {
        (dataLayerItem as { segments: string }).segments = segmentsListString;
      }
    }
  }

  pushNavBar(path: string) {
    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: {
            action: 'click',
            category: EventCategoryGA.NAVBAR,
            label: path,
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.NAVBAR_CLICK,
      event_category: GA4EventCategories.SITE_INTERACTION,
      value: path,
      event_label: formatEventLabel(
        GA4EventLabels.KNECT365_NAVIGATION_LINK_CLICKED,
        { path },
      ),
    });
  }

  pushFormType(formType: string, label: string) {
    const value = formType === 'REGISTRATION' ? 'Sign-up form' : 'Update form';
    const category =
      formType === 'agenda'
        ? GA4EventCategories.LEADGEN
        : GA4EventCategories.CONTENT;

    pushLegacyUAEventToDataLayer(VariablesEventKeys.FORMTYPE, value);

    pushGA4Event({
      event: GA4EventNames.FORM_TYPE_SET,
      event_category: category,
      value,
      event_label: formatEventLabel(GA4EventLabels.FORM_TYPE_SET, {
        value,
        label,
      }),
    });
  }

  pushBuy(counter: string) {
    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: {
            action: 'click',
            category: EventCategoryGA.BUY,
            label: 'Buy',
            value: counter,
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.BUY_BUTTON_CLICK,
      event_category: GA4EventCategories.ECOMMERCE,
      event_label: formatEventLabel(GA4EventLabels.BUY_BUTTON_CLICKED, {
        counter,
      }),
      value: counter,
    });
  }

  pushFilter(category: string, label: string, value: string, mode: string) {
    const getCategory = () => {
      if (mode === 'agenda') return GA4EventCategories.SITE_INTERACTION;
      if (mode === 'speakers') return GA4EventCategories.SITE_INTERACTION;
      return GA4EventCategories.SITE_INTERACTION;
    };

    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: {
            action: 'change',
            category,
            label,
            value,
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.FILTER_CHANGE,
      event_category: getCategory(),
      filter_category: category,
      event_label: formatEventLabel(GA4EventLabels.FILTER_CHANGED, {
        category,
        value,
      }),
      filter_value: value,
    });
  }

  pushCheckoutDetails(products: PricingProductType, step: string) {
    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: 'Checkout',
          ecommerce: {
            checkout: {
              actionField: { step },
              products,
            },
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.CHECKOUT_PROGRESS,
      event_category: GA4EventCategories.ECOMMERCE,
      event_label: formatEventLabel(GA4EventLabels.CHECKOUT_DETAILS, { step }),
      checkout_step: step,
      items: products,
    });
  }

  pushProductDetails(packages: PricingProductType) {
    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: 'Product Details',
          ecommerce: {
            detail: {
              products: packages,
            },
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.CHECKOUT_PROGRESS,
      event_category: GA4EventCategories.ECOMMERCE,
      event_label: GA4EventLabels.PRODUCT_DETAILS,
      checkout_step: 'Select packages',
      items: packages,
    });
  }

  pushSession(sessionDetails: string) {
    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.EVENT, LegacyEventKeys.GA],
      [
        'ga-event',
        {
          event: {
            action: 'click',
            category: EventCategoryGA.SESSION,
            label: sessionDetails,
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.SESSION_SET,
      event_category: GA4EventCategories.USER,
      event_label: GA4EventLabels.SESSION_DETAILS,
      value: sessionDetails,
    });
  }

  pushReceipt(orderReceipt: PricingOrderReceipt) {
    const state = this.getAppState();
    const {
      pageConfig: { portfolioId, tenantId } = {},
      siteHeader: { data: { title } = {} } = {},
    } = state;

    const getVariants = (
      packageSelection: PricingOrderReceiptPackageSelection[],
    ) => {
      let variant = '';

      packageSelection.forEach((p) => {
        variant += p.productName;
      });

      return variant;
    };

    const getCategory = (
      packageSelection: PricingOrderReceiptPackageSelection[],
    ) => {
      let category = '';

      const toString = (arr: string[] = []) => {
        let s = '';
        arr.forEach((a) => {
          s += a;
        });
        return s;
      };

      packageSelection.forEach((p) => {
        category += toString(p.optionNames);
      });

      return category;
    };

    const collectProducts = (
      name: string,
      tickets: PricingOrderReceiptTicketPackages[],
    ) => {
      const products: Array<{
        name: string;
        quantity: number;
        variant: string;
        category: string;
        brand: string;
      }> = [];

      tickets.forEach((ticket) => {
        products.push({
          name,
          quantity: ticket.delegates.length,
          variant: getVariants(ticket.packageSelection),
          category: getCategory(ticket.packageSelection),
          brand: name,
        });
      });

      return products;
    };

    pushLegacyUAEventToDataLayer(
      [LegacyEventKeys.ECOMMERCE],
      [
        {
          currencyCode: orderReceipt.currency,
          purchase: {
            actionField: {
              action: 'purchase',
              id: orderReceipt.uuid,
              affiliation: portfolioId ?? tenantId,
              coupon: orderReceipt.promotionCode,
              revenue: orderReceipt.totalPriceIncVat,
              tax: orderReceipt.totalPriceVat,
            },
            products: collectProducts(title ?? '', orderReceipt.ticketPackages),
          },
        },
      ],
    );

    pushGA4Event({
      event: GA4EventNames.PURCHASE,
      event_category: GA4EventCategories.ECOMMERCE,
      transaction_id: orderReceipt.uuid,
      affiliation: portfolioId ?? tenantId,
      value: orderReceipt.totalPriceIncVat,
      tax: orderReceipt.totalPriceVat,
      coupon: orderReceipt.promotionCode,
      currency: orderReceipt.currency,
      items: collectProducts(title ?? '', orderReceipt.ticketPackages),
      event_label: GA4EventLabels.PURCHASE_DETAILS,
    });

    if (orderReceipt) {
      const receiptPushed = sessionStorage.getItem(orderReceipt.uuid);

      if (receiptPushed) {
        return;
      }

      const products = parsePurchasedProductsForGTM(orderReceipt);

      pushLegacyUAEventToDataLayer(
        [LegacyEventKeys.PURCHASE],
        [
          {
            event: 'Purchase',
            ecommerce: {
              purchase: {
                actionField: {
                  id: orderReceipt.uuid,
                  revenue: orderReceipt.totalPriceIncVat,
                  tax: orderReceipt.totalPriceVat,
                  coupon: orderReceipt.promotionCode,
                },
                products,
              },
            },
          },
        ],
      );

      pushGA4Event({
        event: GA4EventNames.PURCHASE,
        event_category: GA4EventCategories.ECOMMERCE,
        event_label: GA4EventLabels.PURCHASE_DETAILS,
        transaction_id: orderReceipt.uuid,
        affiliation: portfolioId ?? tenantId,
        value: orderReceipt.totalPriceIncVat,
        tax: orderReceipt.totalPriceVat,
        coupon: orderReceipt.promotionCode,
        currency: orderReceipt.currency,
        items: products,
      });
    }
    sessionStorage.setItem(orderReceipt.uuid, 'true');
  }

  pushContentType(contentType: string | undefined, label: string) {
    pushLegacyUAEventToDataLayer(
      VariablesEventKeys.CONTENT_TYPE,
      contentType as string,
    );

    pushGA4Event({
      event: GA4EventNames.CONTENT_TYPE_SET,
      value: contentType as string,
      event_category: GA4EventCategories.CONTENT,
      event_label: label,
    });
  }

  pushArticlePortfolio(portfolio: string) {
    pushLegacyUAEventToDataLayer(VariablesEventKeys.MAIN_PORTFOLIO, portfolio);

    pushGA4Event({
      event: GA4EventNames.ARTICLE_SET,
      value: portfolio,
      event_category: GA4EventCategories.CONTENT,
      event_label: GA4EventLabels.ASSOCIATED_PORTFOLIO_WITH_ARTICLE,
    });
  }

  pushDivisionOption(division: PricingProductsMeta) {
    if (!division) {
      return;
    }

    const { divisionId, divisionName, divisionInfrontBusinessUnit } = division;

    const gaPushFunc = (data: Record<string, string>) =>
      pushGA4Event({
        event: GA4EventNames.DIVISION_OPTIONS_SET,
        event_category: GA4EventCategories.ECOMMERCE,
        event_label: GA4EventLabels.DIVISION_DETAILS,
        ...data,
      });

    if (divisionId) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.DIVISION_OPTION_ID,
        divisionId,
      );
      gaPushFunc({ divisionId });
    }

    if (divisionName) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.DIVISION_OPTION_NAME,
        divisionName,
      );
      gaPushFunc({ divisionName });
    }

    if (divisionInfrontBusinessUnit) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.DIVISION_OPTION_INFRONT_BUSINESS_UNIT,
        divisionInfrontBusinessUnit,
      );
      gaPushFunc({ divisionInfrontBusinessUnit });
    }
  }

  pushHubOptionsData(data: AudienceHubOptions | BrandHubOptions) {
    const { subBrandName, l1Topics, l2Topic, linkedSubBrandNames } = data;

    const gaPushFunc = (data: Record<string, string[]>) =>
      pushGA4Event({
        event: GA4EventNames.HUB_OPTIONS_SET,
        event_category: GA4EventCategories.CONTENT,
        event_label: GA4EventLabels.HUB_OPTIONS_DETAILS,
        ...data,
      });

    if (subBrandName) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.BRAND, [subBrandName]);
    }

    if (l1Topics) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L1_TOPICS, l1Topics);
      gaPushFunc({ l1Topics });
    }

    if (l2Topic) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L2_TOPICS, [l2Topic]);
      gaPushFunc({ l2Topic: [l2Topic] });
    }

    if (linkedSubBrandNames) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.LINKED_BRANDS,
        linkedSubBrandNames,
      );
      gaPushFunc({ linkedSubBrandNames });
    }
  }

  // https://itmebusiness.atlassian.net/browse/KT-1266
  // https://itmebusiness.atlassian.net/browse/FC-5693
  pushHubProductCodeData(data: AudienceHubOptions | BrandHubOptions) {
    const {
      id,
      name,
      domain,
      path,
      type,
      brandUrlSuffix,
      digitalSponsorshipCode,
      newsletterCode,
      productCode,
    } = data;

    pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNHUB, {
      id,
      name,
      domain,
      subDomain: brandUrlSuffix,
      path,
      type,
      products: {
        promotionalEmailsCode: productCode,
        newsletterCode,
        digitalSponsorshipCode,
      },
    });

    pushGA4Event({
      event: GA4EventNames.HUB_PRODUCT_CODE_SET,
      event_category: GA4EventCategories.CONTENT,
      event_label: GA4EventLabels.HUB_PRODUCT_CODE_DETAILS,
      id,
      name,
      domain,
      subDomain: brandUrlSuffix,
      path,
      type,
      promotionalEmailsCode: productCode,
      newsletterCode,
      digitalSponsorshipCode,
    });
  }

  setLegacyUABaseScope() {
    const state = this.getAppState();
    const { sitePath = '', portfolioId = '', tenantId = '' } = state.pageConfig;

    pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNEVENT, {
      domain: portfolioId ?? tenantId,
      path: sitePath.length > 1 ? sitePath : '',
    });
  }

  initGA4BaseContext() {
    const state = this.getAppState();
    const { sitePath = '', portfolioId = '', tenantId = '' } = state.pageConfig;
    const {
      siteType: { data: { type = '' } = {} },
      siteHeader: {
        data: {
          title,
          eventId = '',
          eventCode = '',
          daysToEventEnd = '',
          l1Topics = '',
          l2Topics = '',
          courseId = '',
          courseCode = '',
          daysToCourseEnd = '',
          mainL2Topic = '',
        } = {},
      } = {},
      options: {
        data: {
          subBrands = [],
          division: {
            id: divisionId,
            name: divisionName,
            infrontBusinessUnitId,
          } = {},
        } = {},
      } = {},
      industryHeader: { data: { l1Topics: industryL1Topics } = {} },
      audienceHeader: { data: audienceHeaderData },
    } = state;

    const isEvent = type === EVENT;
    const knnEventId = isEvent ? eventId : courseId;
    const knnEventCode = isEvent ? eventCode : courseCode;
    const knnDaysToEnd = (isEvent ? daysToEventEnd : daysToCourseEnd) || '0';

    // Article block
    const article = state?.article?.home?.data?.data;
    const forename = article?.author?.forename ?? '';
    const surname = article?.author?.surname ?? '';
    const authorName = `${forename} ${surname}`.trim();
    const articleData = article?.articleId
      ? compact({
          article_id: article.articleId,
          article_title: article.title,
          article_path: article.communityPath,
          article_publication_date: article.datePublished,
          article_author: authorName || undefined,
          article_related_topics: article.relatedTopics ?? [],
        })
      : {};

    initGA4Context(
      compact({
        domain: portfolioId || tenantId || undefined,
        path: sitePath.length > 1 ? sitePath : undefined,

        knnevent_title: title ?? undefined,
        knnevent_id: knnEventId ?? undefined,
        knnevent_code: knnEventCode ?? undefined,
        knnevent_days: knnDaysToEnd ?? undefined,
        knnevent_l1_topics: l1Topics ?? [],
        knnevent_l2_topics: l2Topics ?? [],
        knnevent_division_id: divisionId ?? undefined,
        knnevent_division_name: divisionName ?? undefined,
        knnevent_infront_business_unit_id: infrontBusinessUnitId ?? undefined,
        knnevent_main_l2_topic: mainL2Topic ?? undefined,
        industry_l1_topics: industryL1Topics ?? undefined,
        audience_l1_topics: audienceHeaderData?.l1Topics ?? [],
        audience_l2_topics: audienceHeaderData?.l2Topics ?? [],
        sub_brands: subBrands ?? [],
        ...articleData,
      }),
    );
  }

  setEventScope() {
    const state = this.getAppState();
    const {
      siteType: { data: { type = '' } = {} },
      siteHeader: {
        data: {
          title,
          eventId = '',
          eventCode = '',
          daysToEventEnd = '',
          l1Topics = '',
          l2Topics = '',
          courseId = '',
          courseCode = '',
          daysToCourseEnd = '',
          mainL2Topic = '',
        } = {},
      } = {},
      options: {
        data: {
          division: {
            id: divisionId,
            name: divisionName,
            infrontBusinessUnitId,
          } = {},
        } = {},
      } = {},
    } = state;

    if (title) {
      const isEvent = type === EVENT;
      pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNEVENT_NAME, title);
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.KNNEVENT_EVENT_ID,
        isEvent ? eventId : courseId,
      );
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.KNNEVENT_EVENT_CODE,
        isEvent ? eventCode : courseCode,
      );
      const knnEventDaysToEnd = isEvent ? daysToEventEnd : daysToCourseEnd;
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.KNNEVENT_DAYS_TO_EVENT_END,
        knnEventDaysToEnd === 'null' ? '0' : knnEventDaysToEnd,
      );
    }

    if (l1Topics) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L1_TOPICS, l1Topics);
    }

    if (l2Topics) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L2_TOPICS, l2Topics);
    }

    if (divisionId) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.DIVISION_ID, divisionId);
    }

    if (divisionName) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.DIVISION_NAME,
        divisionName,
      );
    }

    if (infrontBusinessUnitId) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.DIVISION_INFRONT_BUSINESS_UNIT,
        infrontBusinessUnitId,
      );
    }

    pushLegacyUAEventToDataLayer(
      VariablesEventKeys.MAIN_TRACKING_TOPIC,
      mainL2Topic,
    );
  }

  isContentGated(value: PushValue) {
    pushLegacyUAEventToDataLayer(VariablesEventKeys.GATED_CONTENT, value);

    pushGA4Event({
      event: GA4EventNames.CONTENT_GATED,
      value,
      event_category: GA4EventCategories.CONTENT,
      event_label: GA4EventLabels.GATED_CONTENT_ACCESS_REQUIRED,
    });
  }

  isUserLoggedIn(value: PushValue) {
    pushLegacyUAEventToDataLayer(VariablesEventKeys.USER_LOGGED_IN, value);

    pushGA4Event({
      event: GA4EventNames.USER_LOGGED_IN_SET,
      value,
      event_category: GA4EventCategories.USER,
      event_label: GA4EventLabels.USER_LOGIN_STATE_DETECTED,
    });
  }

  pushUserID(value: PushValue) {
    pushLegacyUAEventToDataLayer(VariablesEventKeys.USER_ID, value);

    pushGA4Event({
      event: GA4EventNames.USER_ID_SET,
      value,
      event_category: GA4EventCategories.USER,
      event_label: GA4EventLabels.USER_ID_RETRIEVED_FROM_COGNITO,
    });
  }

  setIndustryHomeScope() {
    const state = this.getAppState();
    const { industryHeader: { data: { l1Topics } = {} } = {} } = state;

    if (l1Topics) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L1_TOPICS, l1Topics);
    }
  }

  setAudienceHomeScope() {
    const state = this.getAppState();
    const { audienceHeader: { data: { l1Topics, l2Topics } = {} } = {} } =
      state;

    if (l2Topics) {
      pushLegacyUAEventToDataLayer(
        VariablesEventKeys.L1_TOPICS,
        l1Topics as PushValue,
      );
      pushLegacyUAEventToDataLayer(VariablesEventKeys.L2_TOPICS, l2Topics);
    }
  }

  setArticleScope() {
    const state = this.getAppState();
    const {
      articleHome: {
        data: {
          author: { forename = '', surname = '' } = {},
          communityPath,
          articleId,
          datePublished,
          title,
          relatedTopics,
        } = {},
      } = {},
    } = state;

    if (articleId) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNARTICLE, {
        authorName: `${forename} ${surname}`,
        communityPath,
        id: articleId,
        publicationDate: datePublished,
        title,
        relatedTopics,
      });
    }
  }

  setNewArticleScope() {
    const state = this.getAppState();
    const {
      data: {
        author: { forename = '', surname = '' } = {},
        communityPath,
        articleId,
        datePublished,
        title,
        relatedTopics,
      } = {},
    } = state.article.home.data;

    if (articleId) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNARTICLE, {
        id: articleId,
        title,
        communityPath,
        publicationDate: datePublished,
        authorName: `${forename} ${surname}`,
        relatedTopics,
      });
    }
  }

  setSegmentsScope() {
    const state = this.getAppState();
    const maConfig = state?.options?.data?.maConfig ?? {};
    let segment: string | undefined = maConfig['x-ma-segments'];

    if (!segment) {
      segment = getCookieValue('TUMGM');
    }

    if (segment) {
      this.pushSegment([segment]);
    }
  }

  setSubBrands() {
    const state = this.getAppState();
    const {
      options: {
        data: { subBrands = [] },
      },
    } = state;

    if (subBrands.length) {
      pushLegacyUAEventToDataLayer(VariablesEventKeys.BRAND, subBrands);
    }
  }

  setDataLayer() {
    // Legacy UA setup
    this.setLegacyUABaseScope();
    this.setEventScope();
    this.setIndustryHomeScope();
    this.setAudienceHomeScope();
    this.setArticleScope();
    this.setNewArticleScope();
    this.setSegmentsScope();
    this.setSubBrands();

    // GA4 setup context
    this.initGA4BaseContext();
  }

  pushGoogleAnalyticsId(id: UniqueIdentifier, siteType: string) {
    pushLegacyUAEventToDataLayer(VariablesEventKeys.KNNEVENT_ANALYTICS, id);

    pushGA4Event({
      event: GA4EventNames.ANALYTICS_ID_SET,
      id,
      event_category: GA4EventCategories.CONTENT,
      event_label: formatEventLabel(
        GA4EventLabels.GOOGLE_ANALYTICS_ID_FOR_SITE,
        { siteType },
      ),
    });
  }

  getCustomGoogleTagManagerIdsBySiteType(siteType: string) {
    const state = this.getAppState();

    switch (siteType) {
      case BRAND_HUB:
        return state.brandHub.options.data.googleTagManagerIds;

      case ARTICLE:
      case AUDIENCE_HUB:
        return state.audienceHUB.options.data.googleTagManagerIds;

      case L1_TOPIC_HUB:
        // @ts-ignore
        return state.brandHub.industryHeader.data.googleTagManagerIds;

      case EVENT:
      case COURSE:
        return state.siteHeader.data.googleTagManagerIds;

      default:
        return [];
    }
  }

  addGoogleTagManagerContainers(ids = []) {
    const newContainers = ids.map((gtmId) => {
      return { gtmId, dataLayer: this.googleTagManagerDataLayerConfig };
    });

    this.googleTagManagerContainers = [
      ...this.googleTagManagerContainers,
      ...newContainers,
    ];
  }

  setupGoogleTagManager() {
    const {
      siteType: { data: { type: siteType } = {} },
      options: {
        data: { division: { id: divisionId } = {} },
      },
      pageConfig: { tenantId },
    } = this.getAppState();

    this.googleTagManagerConfig = clientConfig.googleTagManagerConfig;
    this.googleTagManagerDataLayerConfig =
      this.googleTagManagerConfig.dataLayer;

    const ids =
      this.googleTagManagerConfig.tenants[tenantId] ??
      this.googleTagManagerConfig.tenants.DEFAULT;

    this.googleTagManagerContainers = ids.map((gtmId: string) => ({
      gtmId,
      dataLayer: this.googleTagManagerDataLayerConfig,
    }));

    this.addGoogleTagManagerContainers(
      this.googleTagManagerConfig.divisions[divisionId as string],
    );

    this.addGoogleTagManagerContainers(
      this.getCustomGoogleTagManagerIdsBySiteType(siteType as string),
    );

    this.googleTagManagerContainers = uniqBy(
      this.googleTagManagerContainers,
      (o) => o.gtmId,
    );

    this.googleTagManagerContainers.forEach((container) => {
      this.gtm.initialize(container);
    });
  }

  ga4TrackPaymentType(cardType: string, paymentOption: string) {
    pushGA4Event({
      event: GA4EventNames.PAYMENT_TYPE,
      event_category: GA4EventCategories.ECOMMERCE,
      event_label: formatEventLabel(GA4EventLabels.PAYMENT_TYPE, {
        payment_option: paymentOption,
      }),
      card_type: cardType,
      payment_type: paymentOption,
    });
  }

  ga4CTAClick(data: CTAClickType) {
    pushGA4Event({
      event: GA4EventNames.CTA_CLICK,
      event_category: GA4EventCategories.CUSTOM_MARKETING_INTERACTION,
      ...data,
      event_label: formatEventLabel(GA4EventLabels.CTA_CLICK, {
        cta_title: data.cta_title,
        module_type: data.module_type,
      }),
    });
  }

  ga4DownloadClick(data: CTAClickType) {
    pushGA4Event({
      event: GA4EventNames.DOWNLOAD_CLICK,
      event_category: GA4EventCategories.CUSTOM_MARKETING_INTERACTION,
      ...data,
      event_label: formatEventLabel(GA4EventLabels.CTA_DOWNLOAD, {
        cta_title: data.cta_title,
      }),
    });
  }

  ga4VideoPlay(data: CTAClickType) {
    pushGA4Event({
      event: GA4EventNames.VIDEO_PLAY_CLICK,
      event_category: GA4EventCategories.CUSTOM_MARKETING_INTERACTION,
      ...data,
      event_label: formatEventLabel(GA4EventLabels.VIDEO_PLAY, {
        video_title: data.video_title,
      }),
    });
  }

  ga4UserSignUp(label: string, formId: string, formTitle?: string) {
    pushGA4Event({
      event: GA4EventNames.SIGN_UP,
      event_category: GA4EventCategories.USER,
      event_label: label,
      form_id: formId,
      form_title: formTitle ?? '',
    });
  }

  ga4UserLogin(label: string, formId: string, formTitle?: string) {
    pushGA4Event({
      event: GA4EventNames.LOGIN,
      event_category: GA4EventCategories.USER,
      event_label: label,
      form_id: formId,
      form_title: formTitle ?? '',
    });
  }

  ga4UserLogout() {
    pushGA4Event({
      event: GA4EventNames.LOGOUT,
      event_category: GA4EventCategories.USER,
      event_label: 'User logout',
    });
  }

  ga4GenerateLead(data: GenerateLeadType) {
    pushGA4Event({
      event: GA4EventNames.GENERATE_LEAD,
      event_category: GA4EventCategories.LEADGEN,
      ...data,
    });
  }

  patchGPTPrivacySettings() {
    const googletag = window.googletag;
    const pubads = googletag?.pubads;

    if (pubads && typeof pubads === 'function') {
      googletag.cmd = googletag.cmd || {
        push: (func: () => void) => {
          func();
        },
      };

      googletag.cmd.push(() => {
        try {
          pubads().setPrivacySettings({
            childDirectedTreatment: false,
            underAgeOfConsent: false,
            nonPersonalizedAds: false,
          });
          console.log('[GTM] GPT privacy settings applied');
        } catch (e) {
          console.warn('[GTM] Failed to apply privacy settings', e);
        }
      });
    } else {
      setTimeout(() => this.patchGPTPrivacySettings(), 200);
    }
  }

  public init(): void {
    if (isNode() || isEnv('local')) return;

    const { externalScripts } = qs.parse(document.location.search, {
      ignoreQueryPrefix: true,
    });
    if (externalScripts === 'disabled') return;

    this.counter = this.counter ?? 0;

    this.setDataLayer();
    this.setupGoogleTagManager();

    // https://itmebusiness.atlassian.net/browse/KT-2967
    // We are seeing a browser console warning about deprecated GPT methods:
    // The following functions are deprecated: googletag.pubads().setTagForChildDirectedTreatment(), clearTagForChildDirectedTreatment(), setRequestNonPersonalizedAds(), and setTagForUnderAgeOfConsent().
    // Although these methods are not used directly in our codebase, they are likely being invoked by third-party scripts loaded via GTM,
    // such as CMPs or templates. This may lead to incorrect ad behavior or delivery issues.
    // To address this, we’ve implemented a patch using googletag.cmd.push() to override deprecated calls with the recommended setPrivacySettings() method immediately after GTM is initialized.
    this.patchGPTPrivacySettings();
  }
}

const gtmService = new GTMService();

export default gtmService;
